'use client';

import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';
import { Button } from '../ui';

interface FormData {
  name: string;
  email: string;
  // Add source tracking fields
  sourcePage?: string;
  sourceType?: 'blog' | 'news' | 'thought_leadership' | null;
}

interface FormState {
  isSubmitting: boolean;
  error: string | null;
  success: string | null;
  fieldErrors: {
    name?: string;
    email?: string;
  };
}

const SidebarSubscribeForm = () => {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
  });

  const [touchedFields, setTouchedFields] = useState<{ [key: string]: boolean }>({});

  // Get current pathname to determine source
  const pathname = usePathname();

  const [formState, setFormState] = useState<FormState>({
    isSubmitting: false,
    error: null,
    success: null,
    fieldErrors: {},
  });

  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showAlreadySubscribedModal, setShowAlreadySubscribedModal] = useState(false);
  // New state to track if user has completed subscription process
  const [isSubscriptionCompleted, setIsSubscriptionCompleted] = useState(false);

  // Determine source type based on pathname
  useEffect(() => {
    if (pathname) {
      let sourceType: 'blog' | 'news' | 'thought_leadership' | null = null;
      const sourcePage = pathname;

      if (pathname.includes('/blogs/')) {
        sourceType = 'blog';
      } else if (pathname.includes('/news-pr/') || pathname.includes('/news/')) {
        sourceType = 'news';
      } else if (pathname.includes('/thought-leadership/')) {
        sourceType = 'thought_leadership';
      }

      setFormData(prev => ({
        ...prev,
        sourcePage,
        sourceType
      }));
    }
  }, [pathname]);

  // Auto-close modals after 2 seconds
  useEffect(() => {
    if (showSuccessModal) {
      const timer = setTimeout(() => {
        setShowSuccessModal(false);
      }, 2000);
      return () => clearTimeout(timer);
    }
    return undefined; // Add explicit return for all code paths
  }, [showSuccessModal]);

  useEffect(() => {
    if (showAlreadySubscribedModal) {
      const timer = setTimeout(() => {
        setShowAlreadySubscribedModal(false);
      }, 2000);
      return () => clearTimeout(timer);
    }
    return undefined; // Add explicit return for all code paths
  }, [showAlreadySubscribedModal]);

  const validateField = (name: string, value: string, force = false): string | undefined => {
    if (!force && !touchedFields[name]) return undefined;

    if (name === 'name') {
      return value.trim().length < 2 ? 'Name too short' : undefined;
    }
    if (name === 'email') {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return !emailRegex.test(value) ? 'Invalid email' : undefined;
    }
    return undefined;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setTouchedFields((prev) => ({ ...prev, [name]: true }));

    // Clear error when user starts typing
    if (formState.error) setFormState((prev) => ({ ...prev, error: null }));
    if (formState.success) setFormState((prev) => ({ ...prev, success: null }));

    const error = validateField(name, value);
    setFormState((prev) => ({
      ...prev,
      fieldErrors: { ...prev.fieldErrors, [name]: error },
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const errors: { [key: string]: string } = {};
    (Object.keys(formData) as (keyof FormData)[]).forEach((key) => {
      // Skip validation for source tracking fields
      if (key === 'sourcePage' || key === 'sourceType') return;

      const err = validateField(key, formData[key] as string, true);
      if (err) errors[key] = err;
    });

    if (Object.keys(errors).length > 0) {
      setFormState((prev) => ({ ...prev, fieldErrors: errors }));
      return;
    }

    setFormState({ isSubmitting: true, error: null, success: null, fieldErrors: {} });

    try {
      const submitData = new FormData();
      submitData.append('name', formData.name.trim());
      submitData.append('email', formData.email.trim());

      // Add source tracking data
      if (formData.sourcePage) {
        submitData.append('sourcePage', formData.sourcePage);
      }
      if (formData.sourceType) {
        submitData.append('sourceType', formData.sourceType);
      }

      const res = await fetch('/api/subscribe', {
        method: 'POST',
        body: submitData,
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || 'Subscription failed');
      }

      // Handle response based on message type
      if (data.message === 'Already subscribed') {
        // Show already subscribed modal and set appropriate success message
        setShowAlreadySubscribedModal(true);
        setFormState({
          isSubmitting: false,
          error: null,
          success: 'You are already subscribed to our newsletter!',
          fieldErrors: {},
        });
        // Mark subscription as completed
        setIsSubscriptionCompleted(true);
      } else {
        // Show success modal for new subscriber
        setFormData({ name: '', email: '' });
        setTouchedFields({});
        setShowSuccessModal(true);
        setFormState({
          isSubmitting: false,
          error: null,
          success: 'Subscribed successfully!',
          fieldErrors: {},
        });
        // Mark subscription as completed
        setIsSubscriptionCompleted(true);
      }

    } catch (err) {
      setFormState({
        isSubmitting: false,
        error: err instanceof Error ? err.message : 'Something went wrong',
        success: null,
        fieldErrors: {},
      });
    }
  };

  // Modal markup for new subscribers
  const renderSuccessModal = () => (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-sm w-full p-8 text-center animate-in fade-in-zoom duration-300">
        {/* Glow Effect */}
        <div className="absolute -inset-1 rounded-2xl bg-gradient-to-r from-blue-200 to-green-200 blur-lg opacity-30"></div>

        <div className="relative z-10">
          {/* Success Icon */}
          <div className="mx-auto flex items-center justify-center h-20 w-20 rounded-full bg-gradient-to-br from-green-100 to-green-200 shadow-inner">
            <svg
              className="h-10 w-10 text-green-600 animate-scaleIn"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>

          {/* Text Content */}
          <h3 className="mt-6 text-2xl font-bold text-gray-900 tracking-tight">
            Thank You!
          </h3>
          <p className="mt-2 text-gray-600">
            You&apos;ve successfully subscribed. Inspiring stories and insights are on
            their way to your inbox ✨
          </p>

          {/* Auto-close message */}
          <p className="mt-4 text-xs text-gray-400">
            This window will close automatically in 3 seconds
          </p>
        </div>
      </div>

      <style jsx>{`
        @keyframes scaleIn {
          0% {
            transform: scale(0.8);
            opacity: 0;
          }
          100% {
            transform: scale(1);
            opacity: 1;
          }
        }
        .animate-scaleIn {
          animation: scaleIn 0.4s ease-out forwards;
        }
      `}</style>
    </div>
  );

  // Modal markup for already subscribed users
  const renderAlreadySubscribedModal = () => (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-sm w-full p-8 text-center animate-in fade-in-zoom duration-300">
        {/* Glow Effect */}
        <div className="absolute -inset-1 rounded-2xl bg-gradient-to-r from-purple-200 to-blue-200 blur-lg opacity-30"></div>

        <div className="relative z-10">
          {/* Info Icon */}
          <div className="mx-auto flex items-center justify-center h-20 w-20 rounded-full bg-gradient-to-br from-purple-100 to-blue-100 shadow-inner">
            <svg
              className="h-10 w-10 text-purple-600 animate-scaleIn"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>

          {/* Text Content */}
          <h3 className="mt-6 text-2xl font-bold text-gray-900 tracking-tight">
            Hey there!
          </h3>
          <p className="mt-2 text-gray-600">
            You&apos;ve already subscribed — thanks for being with us!
          </p>

          {/* Auto-close message */}
          <p className="mt-4 text-xs text-gray-400">
            This window will close automatically in 2 seconds
          </p>
        </div>
      </div>

      <style jsx>{`
        @keyframes scaleIn {
          0% {
            transform: scale(0.8);
            opacity: 0;
          }
          100% {
            transform: scale(1);
            opacity: 1;
          }
        }
        .animate-scaleIn {
          animation: scaleIn 0.4s ease-out forwards;
        }
      `}</style>
    </div>
  );

  return (
    <>
      <aside className="w-full mx-auto p-6 bg-white rounded-lg shadow-md border border-gray-100">
        {/* Icon + Heading */}
        <div className="flex items-center gap-3 mb-5">
          <div className="p-2 bg-blue-100 rounded-full">
            <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
            </svg>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Stay Updated</h3>
            <p className="text-sm text-gray-600">Get tips & news in your inbox</p>
          </div>
        </div>

        {/* Success Message */}
        {formState.success && !showSuccessModal && !showAlreadySubscribedModal && (
          <div className="mb-4 p-3 text-sm text-green-700 bg-green-50 border border-green-200 rounded-md">
            {formState.success}
          </div>
        )}

        {/* Global Error */}
        {formState.error && (
          <div className="mb-4 p-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md">
            {formState.error}
          </div>
        )}

        {/* Form - hide after successful subscription or already subscribed */}
        {!isSubscriptionCompleted && (
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Name */}
            <div>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Your Name"
                className={`w-full px-4 py-2.5 text-sm border rounded-md focus:outline-none  transition ${formState.fieldErrors.name
                  ? 'border-red-400 focus:ring-red-500'
                  : 'border-gray-300'
                  }`}
                required
              />
              {formState.fieldErrors.name && touchedFields.name && (
                <p className="mt-1 text-xs text-red-600">{formState.fieldErrors.name}</p>
              )}
            </div>

            {/* Email */}
            <div>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="you@example.com"
                className={`w-full px-4 py-2.5 text-sm border rounded-md focus:outline-none  transition ${formState.fieldErrors.email
                  ? 'border-red-400 focus:ring-red-500'
                  : 'border-gray-300'
                  }`}
                required
              />
              {formState.fieldErrors.email && touchedFields.email && (
                <p className="mt-1 text-xs text-red-600">{formState.fieldErrors.email}</p>
              )}
            </div>

            {/* Submit */}
            <Button
              type="submit"
              variant="primary"
              className="w-full"
              disabled={formState.isSubmitting}
            >
              {formState.isSubmitting ? (
                <span className="flex items-center justify-center">
                  <svg className="w-4 h-4 mr-2 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Subscribing...
                </span>
              ) : (
                'Subscribe'
              )}
            </Button>
          </form>
        )}

        {/* Privacy Note */}
        <p className="mt-4 text-xs text-center text-gray-500">
          Privacy, protected with care.
        </p>
      </aside>

      {/* Success Modal for new subscribers */}
      {showSuccessModal && renderSuccessModal()}

      {/* Modal for already subscribed users */}
      {showAlreadySubscribedModal && renderAlreadySubscribedModal()}
    </>
  );
};

export default SidebarSubscribeForm;
